import { pctToGate } from './sniperArm';
import type { PatchSniperMarketDto } from '../types/sniper.types';

/**
 * A patch to apply to every selected market — only the fields the operator typed.
 * DERIVED from the wire DTO (not hand-duplicated) so the bulk bar can never drift
 * from the contract it writes to.
 */
export type SniperBulkPatch = Pick<
  PatchSniperMarketDto,
  'size' | 'maxPrice' | 'confidenceGate' | 'enabled'
>;

export interface ParsedBulkPatch {
  patch: SniperBulkPatch;
  /** At least one typed field is valid AND none typed is invalid. */
  canApply: boolean;
}

/**
 * Parse the bulk-bar draft strings into a patch carrying ONLY the params the
 * operator typed and that are valid. A blank field is omitted (leave untouched).
 * Validation mirrors `SniperMarketRow` AND the backend DTO: size >= 1 (server is
 * `@Min(1)` — accepting a fractional size here only earns a 400 on the whole
 * bulk), maxPrice ∈ (0,1], gate a percent 1..100 (→ 0..1 via `pctToGate`). Any
 * typed-but-invalid field blocks Apply so a bad value can't silently skip.
 */
export function parseBulkPatch(
  size: string,
  maxPrice: string,
  gate: string,
): ParsedBulkPatch {
  const patch: SniperBulkPatch = {};
  let invalid = false;
  let any = false;

  if (size !== '') {
    const s = Number(size);
    if (Number.isFinite(s) && s >= 1) {
      patch.size = s;
      any = true;
    } else invalid = true;
  }
  if (maxPrice !== '') {
    const m = Number(maxPrice);
    if (Number.isFinite(m) && m > 0 && m <= 1) {
      patch.maxPrice = m;
      any = true;
    } else invalid = true;
  }
  if (gate !== '') {
    const g = pctToGate(Number(gate)); // 1..100 → 0..1, else null
    if (g !== null) {
      patch.confidenceGate = g;
      any = true;
    } else invalid = true;
  }

  return { patch, canApply: any && !invalid };
}
